"use client"

import {
  getCatalogVisibilityMode,
  shouldResolveCatalogVisibility,
} from "@/lib/catalog-visibility"
import { DEFAULT_MARKET_CHANNEL, isOciMarketChannel, type MarketChannel } from "@/lib/market/market-channel"

interface StorefrontContextResponse {
  isAnonymous: boolean
  oci: boolean
}

async function fetchStorefrontContext(
  sbLocale: string,
  channel: MarketChannel,
  signal: AbortSignal
): Promise<StorefrontContextResponse | undefined> {
  const contextSearchParams = new URLSearchParams()
  contextSearchParams.set("sbLocale", sbLocale)
  contextSearchParams.set("channel", channel)

  const response = await fetch(`/api/storefront/context?${contextSearchParams.toString()}`, {
    cache: "no-store",
    signal,
  })

  if (!response.ok) {
    return undefined
  }

  return response.json() as Promise<StorefrontContextResponse>
}

export async function resolveListingApiUrl(
  listingType: "category" | "search" | "brand",
  searchParams: URLSearchParams,
  {
    sbLocale,
    channel = DEFAULT_MARKET_CHANNEL,
    signal,
  }: {
    sbLocale: string
    channel?: MarketChannel
    signal: AbortSignal
  }
) {
  const directUrl = `/api/storefront/listing/${listingType}?${searchParams.toString()}`

  if (!isOciMarketChannel(channel)) {
    return directUrl
  }

  const context = await fetchStorefrontContext(sbLocale, channel, signal)
  const effectiveChannel: MarketChannel = context?.oci ? "oci" : channel
  const shouldUseResolver = shouldResolveCatalogVisibility({
    mode: getCatalogVisibilityMode(effectiveChannel),
    channel: effectiveChannel,
    isAuthenticated: context ? !context.isAnonymous : false,
  })

  return shouldUseResolver ? `/api/storefront/listing-resolver/${listingType}?${searchParams.toString()}` : directUrl
}
